import React, { useEffect, useRef, useState } from "react";
import "./Roulette.scss";
import { rouletteMusic } from "./audio";

const BASE_SLOTS = [
  { multiplier: 1.5,  cssClass: "slot-1_5x" },
  { multiplier: 1.8,  cssClass: "slot-1_8x" },
  { multiplier: 2,    cssClass: "slot-2x" },
  { multiplier: null, cssClass: "slot-double" },
  { multiplier: 3,    cssClass: "slot-3x" },
  { multiplier: 5,    cssClass: "slot-5x" },
  { multiplier: 50,   cssClass: "slot-50x" },
  { multiplier: 100,  cssClass: "slot-100x" },
  { multiplier: 500,  cssClass: "slot-500x" },
  { multiplier: 1000, cssClass: "slot-1000x" },
];

export function formatSlotLabel(slot, bet) {
  if (slot.cssClass === "slot-double") return "DOBRAR!";
  const value = slot.multiplier * (bet || 0);
  return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

export function doubleSlotMultipliers(slots) {
  return slots.map((s) => {
    if (s.cssClass === "slot-double") return { ...s };
    return { ...s, multiplier: s.multiplier * 2 };
  });
}

const SLOT_HEIGHT = 80;
const SPIN_DURATION_MS = 9000;
const DOUBLE_FLASH_MS = 1800;
const PRIZE_REVEAL_MS = 1500;
const STRIP_REPEATS = 5;

const PHASE = { IDLE: "idle", SPINNING: "spinning", DOUBLE_FLASH: "double_flash", REVEAL: "reveal" };

function getTargetPx(slotIndex) {
  return -((STRIP_REPEATS - 1) * BASE_SLOTS.length + slotIndex) * SLOT_HEIGHT;
}

function Roulette({ spins, prize, bet = 0, onComplete }) {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [slots, setSlots] = useState(BASE_SLOTS);
  // spinIndex tracks which spin in the spins[] array we're about to animate
  const [spinIndex, setSpinIndex] = useState(0);
  const stripRef = useRef(null);
  const activeRef = useRef(false); // prevents double-trigger

  // Pause the shared audio when the roulette unmounts.
  useEffect(() => {
    return () => {
      rouletteMusic.pause();
      rouletteMusic.currentTime = 0;
    };
  }, []);

  function stopMusic() {
    rouletteMusic.pause();
    rouletteMusic.currentTime = 0;
  }

  // Play music once on the first spin, keep it playing through doubles,
  // only stop when the roulette lands on a final result.
  useEffect(() => {
    if (phase === PHASE.SPINNING && rouletteMusic.paused) {
      rouletteMusic.play().catch(() => {});
    } else if (phase === PHASE.REVEAL || phase === PHASE.IDLE) {
      stopMusic();
    }
  }, [phase]);

  function snapToTop() {
    const el = stripRef.current;
    if (!el) return;
    el.style.transition = "none";
    el.style.transform = "translateY(0px)";
    el.getBoundingClientRect();
  }

  function animateTo(slotIndex) {
    const el = stripRef.current;
    if (!el) return;
    el.style.transition = `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.17, 0.67, 0.12, 1.0)`;
    el.style.transform = `translateY(${getTargetPx(slotIndex)}px)`;
  }

  // Trigger a spin whenever phase===SPINNING and spinIndex changes
  useEffect(() => {
    if (phase !== PHASE.SPINNING) return;
    if (activeRef.current) return;
    if (spins[spinIndex] == null) return;

    activeRef.current = true;

    snapToTop();
    setTimeout(() => {
      const slotIndex = spins[spinIndex];
      animateTo(slotIndex);

      setTimeout(() => {
        activeRef.current = false;
        const isDouble = BASE_SLOTS[slotIndex]?.cssClass === "slot-double";

        if (isDouble) {
          // Double the slot multipliers for the next spin, then flash
          setSlots((prev) => doubleSlotMultipliers(prev));
          setPhase(PHASE.DOUBLE_FLASH);
        } else {
          setPhase(PHASE.REVEAL);
          setTimeout(() => onComplete?.(), PRIZE_REVEAL_MS);
        }
      }, SPIN_DURATION_MS);
    }, 50);
  }, [phase, spinIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // After DOUBLE_FLASH, advance to next spin
  useEffect(() => {
    if (phase !== PHASE.DOUBLE_FLASH) return;
    const id = setTimeout(() => {
      setSpinIndex((i) => i + 1);
      setPhase(PHASE.SPINNING);
    }, DOUBLE_FLASH_MS);
    return () => clearTimeout(id);
  }, [phase]);

  // Kick off the first spin
  useEffect(() => {
    if (!spins.length) return;
    setPhase(PHASE.SPINNING);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const strip = [...slots, ...slots, ...slots, ...slots, ...slots];
  const showStrip = phase !== PHASE.DOUBLE_FLASH;

  return (
    <div className="roulette-overlay">
      <h2>Girando prêmio...</h2>

      {showStrip && (
        <div className="roulette-window">
          <div className="roulette-strip" ref={stripRef}>
            {strip.map((slot, i) => (
              <div key={i} className={`roulette-slot ${slot.cssClass}`}>
                {formatSlotLabel(slot, bet)}
              </div>
            ))}
          </div>
        </div>
      )}

      {phase === PHASE.DOUBLE_FLASH && (
        <div className="roulette-double-flash">DOBRAR!</div>
      )}

      {phase === PHASE.REVEAL && (
        <div className="roulette-prize">
          <span className="prize-label">Prêmio</span>
          <span className="prize-amount">
            R$ {prize != null ? Number(prize).toFixed(2) : "0.00"}
          </span>
        </div>
      )}
    </div>
  );
}

export default Roulette;
